export const M6_UNIT_4 = {
  id: "m6-u4-might-possibilities",
  title: "Unit 4: Future Possibilities with MIGHT",
  topic: "Master uncertainty! Learn MIGHT to express possibilities (50/50). Complete your certainty scale: WON'T (0%) → MIGHT (50%) → WILL (100%). Essential for polite, natural English!",
  iconType: "question-circle",
  grammarImage: "/grammar/grammar-m6-u4.jpg",
  grammarText: "Use MIGHT when you're NOT SURE about the future (50% possibility). Different from WILL (certain) and WON'T (certain negative). MIGHT doesn't change with subject and is NOT used in questions!",

  sets: [

    // ===================================================================
    // SET 1: INTRODUCING MIGHT
    // ===================================================================
    {
      id: "m6-u4-s1",
      title: "Set 1: What is MIGHT?",
      context: `*Toronto — Week 12, Thursday evening*

Sofía is texting with her friend Priya about the weekend. She isn't sure yet what she wants to do.
"Are you coming to the market Saturday?" Priya asks.
Sofía thinks for a moment. "I might go," she types. "I'm not sure yet."
Priya laughs. "That's very Canadian of you! We never commit to anything!"

*Toronto — Semana 12, jueves por la noche*

Sofía le escribe a su amiga Priya sobre el fin de semana. Todavía no sabe qué quiere hacer.
"¿Vendrás al mercado el sábado?" pregunta Priya.
Sofía piensa un momento. "Quizás vaya," escribe. "Todavía no estoy segura."
Priya se ríe. "¡Eso es muy canadiense de tu parte! ¡Nunca nos comprometemos con nada!"`,

      questions: [
        {
          type: "mc",
          q: "Which sentence shows you're NOT SURE about the future?",
          options: ["I might go.", "I'll go.", "I won't go."],
          a: "I might go.",
          tip: "MIGHT = maybe! You're uncertain (50/50 possibility).",
          successTip: "MIGHT = the middle ground of certainty 🤔. Natives use MIGHT constantly — it's polite, non-committal, and very natural. 'I might go' = quizás vaya. You're not saying yes or no — just 'maybe'. Perfect for when you genuinely don't know yet!"
        },
        {
          type: "match",
          q: "Match the level of certainty to the correct form:",
          pairs: [
            { left: "0% certain (NO)", right: "I won't go" },
            { left: "50% uncertain (MAYBE)", right: "I might go" },
            { left: "100% certain (YES)", right: "I'll go" }
          ],
          a: "completed",
          successTip: "The certainty scale is the heart of Unit 4! ❌ WON'T (0%) → 🤔 MIGHT (50%) → ✅ WILL (100%). MIGHT sits perfectly in the middle — you're genuinely unsure. Master this scale and you can express any level of certainty in English!"
        },
        {
          type: "order",
          q: "Traduce: Quizás vaya a la fiesta.",
          words: ["I", "might", "go", "to", "the", "party."],
          a: "I might go to the party.",
          extra: ["will", "going", "to"],
          tip: "MIGHT = quizás/tal vez (50% possibility). Always followed by base verb.",
          successTip: "'I might go to the party' = quizás vaya a la fiesta 🎉. Notice: MIGHT + base verb — no 'to', no '-ing'. 'I might go' ✓ / 'I might to go' ❌ / 'I might going' ❌. One simple rule: MIGHT + BASE VERB. That's it!"
        },
        {
          type: "mc",
          q: "Complete: I have some money. I ___ buy a new phone.",
          options: ["might", "will", "won't"],
          a: "might",
          tip: "'Some money' = not sure if enough. Use MIGHT (possibility, not certainty).",
          successTip: "Context clues signal which modal to use 🔑. 'Some money' = maybe enough → MIGHT. 'Lots of money' = definitely enough → I'll buy ✓. 'No money' = definitely not → I won't buy ❌. The context tells you the certainty level!"
        },
        {
          type: "input",
          q: "I'm not sure about tomorrow. I ___ stay home.",
          a: "might",
          alts: ["Might"],
          tip: "'Not sure' = uncertainty signal. Always use MIGHT.",
          successTip: "'I might stay home' = quizás me quede en casa 🏠. 'Not sure' + MIGHT = the most natural combo for uncertainty. You'll use this phrase every week — for weekends, after-work plans, social invitations. Very common and very polite!"
        },
        {
          type: "mc",
          q: "Complete: 'I have no money.' → I ___ buy anything.",
          options: ["won't", "might", "will"],
          a: "won't",
          tip: "NO money = 0% certain. WON'T (not MIGHT — this is definite).",
          successTip: "WON'T vs MIGHT = key distinction ⚠️. WON'T = definite no ❌. MIGHT NOT = maybe no 🤷. 'No money' = 100% certain you can't buy → WON'T. If you said MIGHT, it would suggest you're not sure whether you can buy it — but with zero money, there's no doubt!"
        },
        {
          type: "order",
          q: "Traduce: Tengo mucho dinero. Compraré un carro. (100% certain)",
          words: ["I", "have", "lots", "of", "money.", "I'll", "buy", "a", "car."],
          a: "I have lots of money. I'll buy a car.",
          extra: ["might", "going", "to"],
          tip: "LOTS of money = certain! Use WILL (not MIGHT = uncertain).",
          successTip: "'Lots of money' → I'll buy = 100% certain 💰✅. MIGHT would be wrong here — it would suggest uncertainty when you clearly have enough money. The clue 'lots of' signals certainty, which means WILL. Always let the context guide your modal choice!"
        },
        {
          type: "mc",
          q: "Which sentence shows UNCERTAINTY?",
          options: ["She might come.", "She'll come.", "She won't come."],
          a: "She might come.",
          successTip: "'She might come' = tal vez ella venga 🤷. Notice: MIGHT doesn't change form — it's 'she might' NOT 'she mights'. Modal verbs never add -s for third person. 'He might', 'she might', 'it might' — all the same. This rule applies to all modals!"
        },
        {
          type: "input",
          q: "It looks cloudy. It ___ rain later.",
          a: "might",
          alts: ["Might"],
          tip: "Cloudy = possible rain but not certain. Use MIGHT (not WILL or GOING TO).",
          successTip: "Weather uncertainty = MIGHT ☁️. Compare the three: 'It's going to rain' (you see dark clouds — evidence). 'It will rain' (your prediction/opinion). 'It might rain' (possible but uncertain). For genuine uncertainty about weather, MIGHT is the most natural and honest choice!"
        },
        {
          type: "mc",
          q: "Order from LEAST to MOST certain:",
          options: ["won't → might → will", "might → won't → will", "will → might → won't"],
          a: "won't → might → will",
          successTip: "The complete certainty scale! ❌ WON'T (0%) → 🤔 MIGHT (50%) → ✅ WILL (100%). You now understand the full future certainty spectrum. This is one of the most useful mental models in English — apply it every time you're deciding between modals!"
        }
      ]
    },

    // ===================================================================
    // SET 2: MIGHT IN CONTEXT
    // ===================================================================
    {
      id: "m6-u4-s2",
      title: "Set 2: Using MIGHT",
      context: `*Toronto — Week 12, Friday*

Priya is planning the weekend. She texts the group chat to see who's available.
"Gym Saturday morning?" she asks.
Dan replies: "I might come — depends on how late I stay up tonight."
Sofía types: "I might need an umbrella — it looks like rain. I might stay in."
Nobody commits. Priya laughs. "Classic group chat. Nobody ever says yes or no!"

*Toronto — Semana 12, viernes*

Priya está planeando el fin de semana. Le escribe al grupo para ver quién está disponible.
"¿Gimnasio el sábado por la mañana?" pregunta.
Dan responde: "Quizás vaya — depende de qué tan tarde me quede despierto esta noche."
Sofía escribe: "Quizás necesite un paraguas — parece que va a llover. Quizás me quede en casa."
Nadie se compromete. Priya se ríe. "Chat grupal típico. ¡Nadie dice que sí o que no!"`,

      questions: [
        {
          type: "mc",
          q: "There are some clouds. It ___ rain.",
          options: ["might", "will", "is going to"],
          a: "might",
          tip: "SOME clouds = not sure. MIGHT (not 'going to', which needs strong evidence).",
          successTip: "Weather modals compared 🌤️: 'It might rain' (possible — some clouds). 'It's going to rain' (very likely — dark heavy clouds, you can see it). 'It will rain' (your personal prediction). MIGHT = genuine uncertainty. Natives say 'it might rain' constantly because weather is never 100% sure!"
        },
        {
          type: "order",
          q: "Traduce: Quizás necesite un paraguas más tarde.",
          words: ["I", "might", "need", "an", "umbrella", "later."],
          a: "I might need an umbrella later.",
          extra: ["will", "going", "to"],
          tip: "MIGHT + base verb. 'Need' stays in base form — not 'needing' or 'to need'.",
          successTip: "'I might need an umbrella later' = quizás necesite un paraguas 🌂. 'An umbrella' (not 'a umbrella') — vowel sound rule! Very natural daily phrase. Combine it with weather: 'It might rain, so I might need an umbrella.' Classic uncertainty chain!"
        },
        {
          type: "input",
          q: "I'm tired. I ___ stay in tonight.",
          a: "might",
          alts: ["Might"],
          tip: "'Stay in' = quedarse en casa. Tiredness = uncertainty about going out.",
          successTip: "'Stay in' = quedarse en casa (phrasal verb) 🏠. Opposite: 'go out'. 'I might stay in tonight' = very common Friday/Saturday phrase when you're tired. MIGHT is perfect here — you haven't decided yet. Very natural and frequently used!"
        },
        {
          type: "mc",
          q: "'Are you coming to the party?' 'I don't know. I ___ go.'",
          options: ["might", "will", "am going to"],
          a: "might",
          tip: "'I don't know' = uncertainty signal → always use MIGHT.",
          successTip: "'I don't know. I might go.' = natural honest response 🎉. 'I don't know' sets up MIGHT perfectly. Compare: 'I'll go' (decided). 'I'm going to go' (already planned). 'I might go' (genuinely unsure). This is the most common and honest answer to social invitations!"
        },
        {
          type: "order",
          q: "Traduce: Ella quizás venga con nosotros.",
          words: ["She", "might", "come", "with", "us."],
          a: "She might come with us.",
          extra: ["will", "going", "to"],
          tip: "Third person: 'She might' — NEVER 'she mights'. No -s on modal verbs!",
          successTip: "'She might come with us' = tal vez venga con nosotros 👥. CRITICAL RULE: No -s for third person! 'He mights' ❌ / 'She mights' ❌ → always 'he might' / 'she might' ✓. Modal verbs never change form. This is one of the most common errors to avoid!"
        },
        {
          type: "mc",
          q: "Big life decision: 'We ___ move to Canada next year.'",
          options: ["might", "will", "'re going to"],
          a: "might",
          tip: "Still considering it, not decided = MIGHT. Not going to (= already decided).",
          successTip: "MIGHT for big undecided life plans 🌍. 'We might move to Canada' = we're considering it but haven't decided. Very common for major decisions: jobs, cities, houses. MIGHT buys you time — it's not a commitment, just an open possibility. Much more honest than GOING TO if you're still unsure!"
        },
        {
          type: "input",
          q: "I'm not hungry now, but I ___ eat something later.",
          a: "might",
          alts: ["Might"],
          tip: "Not hungry NOW but open to eating later = possibility = MIGHT.",
          successTip: "'I might eat something later' = quizás coma algo después 🍽️. 'Something' = informal, non-specific. Very natural for talking about future hunger. 'I'll eat later' would mean you're planning to — but you're not sure yet, so MIGHT is more honest and natural!"
        },
        {
          type: "mc",
          q: "Which is MOST uncertain?",
          options: ["I might go.", "I'm going to go.", "I'll go."],
          a: "I might go.",
          tip: "MIGHT = least committed. Most uncertain of all future forms.",
          successTip: "Uncertainty ranking 📊: 'I'll go' (decided now). 'I'm going to go' (already planned). 'I might go' (maybe, not sure). MIGHT = zero commitment. You haven't made a decision at all. For social plans, MIGHT is perfectly acceptable — it's honest and keeps your options open!"
        },
        {
          type: "order",
          q: "Traduce: Quizás compre un carro nuevo el próximo año.",
          words: ["I", "might", "buy", "a", "new", "car", "next", "year."],
          a: "I might buy a new car next year.",
          extra: ["will", "going", "to"],
          tip: "MIGHT works for any time frame — tomorrow, next week, next year.",
          successTip: "'I might buy a new car next year' = quizás compre un carro nuevo el próximo año 🚗. MIGHT works for ANY future timeframe — short-term or long-term. 'I might eat later' (tonight) or 'I might retire early' (years away) — both perfectly natural with MIGHT!"
        },
        {
          type: "match",
          q: "Match the situations to the correct future forms:",
          pairs: [
            { left: "Not sure (50%)", right: "might go" },
            { left: "Definite plan already made", right: "going to go" },
            { left: "Instant decision right now", right: "will go" }
          ],
          a: "completed",
          successTip: "Three futures, three meanings 🎯. MIGHT = uncertain (50%). GOING TO = already planned (evidence/decision before). WILL = spontaneous instant decision. You now know all three and when to use each one. This is the most important grammar triangle in Module 6!"
        }
      ]
    },

    // ===================================================================
    // SET 3: MIGHT NOT
    // ===================================================================
    {
      id: "m6-u4-s3",
      title: "Set 3: MIGHT NOT",
      context: `*Toronto — Week 13, Monday*

Leo has a Spanish exam on Friday and a birthday party invitation for the same night.
"I might not go to the party," he tells Sofía. "I need to study."
Sofía nods. "It might not be a problem — maybe the exam will be easy!"
Leo sighs. "I might not sleep much this week either."
Sofía laughs. "Leo, you might not survive the semester!"

*Toronto — Semana 13, lunes*

Leo tiene un examen de español el viernes y una invitación a una fiesta de cumpleaños la misma noche.
"Quizás no vaya a la fiesta," le dice a Sofía. "Necesito estudiar."
Sofía asiente. "Quizás no sea un problema — ¡a lo mejor el examen es fácil!"
Leo suspira. "Quizás tampoco duerma mucho esta semana."
Sofía se ríe. "¡Leo, quizás no sobrevivas el semestre!"`,

      questions: [
        {
          type: "mc",
          q: "Negative uncertainty: I'm not sure. I ___ go.",
          options: ["might not", "won't", "don't"],
          a: "might not",
          tip: "NOT SURE = uncertain negative. MIGHT NOT (WON'T = 100% no).",
          successTip: "MIGHT NOT vs WON'T = crucial difference ⚠️. WON'T = definite no ❌ (0%). MIGHT NOT = uncertain no 🤷 (roughly 25-40%). 'I might not go' = quizás no vaya — you're leaning towards no but haven't decided. This is very different from WON'T!"
        },
        {
          type: "order",
          q: "Traduce: Quizás no vaya a la fiesta.",
          words: ["I", "might", "not", "go", "to", "the", "party."],
          a: "I might not go to the party.",
          extra: ["won't", "don't", "will"],
          tip: "MIGHT NOT = two separate words! NOT comes between MIGHT and the main verb.",
          successTip: "'I might not go' = quizás no vaya 🎉. Structure: MIGHT + NOT + base verb. Note: In American English, 'mightn't' is rare — always use 'might not'. In British English, 'mightn't' is sometimes used but 'might not' is always safe. Stick with 'might not'!"
        },
        {
          type: "input",
          q: "It depends on the weather. We ___ ___ have the picnic.",
          a: "might not",
          alts: ["Might not"],
          tip: "'Depends on' = conditional/uncertain. Use MIGHT NOT (two words).",
          successTip: "'It depends on the weather. We might not have the picnic' 🌦️. 'It depends on' + MIGHT/MIGHT NOT = super natural pairing. When something depends on an unknown factor, MIGHT NOT is perfect. 'It depends on traffic / money / the weather' → might or might not!"
        },
        {
          type: "mc",
          q: "'I'm definitely NOT going.' Which form is correct?",
          options: ["I won't go.", "I might not go.", "I might go."],
          a: "I won't go.",
          tip: "DEFINITELY NOT = 100% no. Use WON'T (not MIGHT NOT).",
          successTip: "'Definitely not' → WON'T ❌. When you're 100% certain you won't do something, use WON'T. MIGHT NOT implies you're still considering — which contradicts 'definitely'. 'Definitely not going' = WON'T go. The word 'definitely' is your clue to use the absolute form!"
        },
        {
          type: "order",
          q: "Traduce: Ella quizás no venga. Está ocupada.",
          words: ["She", "might", "not", "come.", "She's", "busy."],
          a: "She might not come. She's busy.",
          extra: ["won't", "doesn't", "going"],
          tip: "MIGHT NOT for third person too — no -s. 'She might not' (NEVER 'she mights not').",
          successTip: "'She might not come. She's busy.' = very natural way to explain someone's possible absence 🤷. Notice the reason follows in the next sentence — busy, tired, sick. This two-sentence pattern (uncertainty + reason) sounds completely authentic in everyday conversation!"
        },
        {
          type: "mc",
          q: "US vs UK: Which is MORE COMMON in American English?",
          options: ["I might not go.", "I mightn't go.", "Both equally common."],
          a: "I might not go.",
          tip: "US prefers 'might not' as two words. UK sometimes uses 'mightn't'.",
          successTip: "Regional difference 🇺🇸🇬🇧: 'might not' = standard American English. 'mightn't' = occasionally used in British English. Always use 'might not' — it's safe and understood everywhere. 'Mightn't' appears in British books but is rare in everyday speech even in the UK!"
        },
        {
          type: "input",
          q: "He's sick. He ___ ___ come to work tomorrow.",
          a: "might not",
          alts: ["Might not"],
          tip: "Sick = possible absence, but not certain. MIGHT NOT (not WON'T = too definite).",
          successTip: "'He might not come to work tomorrow' = polite, considerate uncertainty 🤒. Using MIGHT NOT instead of WON'T shows respect — you're not making a definite claim about someone else's decision. Very common and natural when discussing someone who's ill but might still show up!"
        },
        {
          type: "mc",
          q: "Which shows roughly 25% possibility (leaning towards NO)?",
          options: ["I might not go.", "I won't go.", "I might go."],
          a: "I might not go.",
          successTip: "Probability nuance 📊: MIGHT = neutral 50% (true maybe). MIGHT NOT ≈ 25% (leaning towards no, but not certain). WON'T = 0% (definite no). These three cover the negative spectrum. 'I might not go' = quizás no vaya — you're probably not going, but haven't decided for certain."
        },
        {
          type: "order",
          q: "Traduce: Quizás no llueva. El cielo se está despejando.",
          words: ["It", "might", "not", "rain.", "The", "sky", "is", "clearing", "up."],
          a: "It might not rain. The sky is clearing up.",
          extra: ["won't", "going", "to"],
          tip: "Sky clearing = positive sign, but still uncertain. MIGHT NOT (not WON'T).",
          successTip: "'Clear up' = despejarse ☀️. Essential weather phrasal verb! 'The sky is clearing up' → positive evidence, but still uncertain → MIGHT NOT rain. Compare: 'It won't rain' (100% sure) vs 'It might not rain' (leaning towards no, but not certain). Sky clearing = MIGHT NOT, not WON'T!"
        },
        {
          type: "match",
          q: "Match the negative certainty levels:",
          pairs: [
            { left: "0% (definite no)", right: "won't" },
            { left: "25% (leaning towards no)", right: "might not" },
            { left: "50% (true maybe)", right: "might" }
          ],
          a: "completed",
          successTip: "Complete negative certainty spectrum! 🎯 WON'T (0%) = absolutely not. MIGHT NOT (25%) = probably not, but unsure. MIGHT (50%) = genuinely undecided. These three forms cover every level of negative/uncertain future in English. You now have full control!"
        }
      ]
    },

    // ===================================================================
    // SET 4: "I'M NOT SURE" + MIGHT
    // ===================================================================
    {
      id: "m6-u4-s4",
      title: "Set 4: Emphasizing Uncertainty",
      context: `*Toronto — Week 13, Wednesday lunch*

Amara is the most thoughtful person in the group. She never commits to anything without thinking first.
"Are you coming to the study group tonight?" Sofía asks.
Amara pauses. "I'm not sure. I might come. I might not. I haven't decided yet."
Sofía smiles. "Amara, you might be the most uncertain person I've ever met!"
Amara laughs. "Or the most honest!"

*Toronto — Semana 13, almuerzo del miércoles*

Amara es la persona más reflexiva del grupo. Nunca se compromete con nada sin pensarlo primero.
"¿Vendrás al grupo de estudio esta noche?" pregunta Sofía.
Amara hace una pausa. "No estoy segura. Quizás vaya. Quizás no. Todavía no lo he decidido."
Sofía sonríe. "Amara, ¡quizás seas la persona más indecisa que he conocido!"
Amara se ríe. "¡O la más honesta!"`,

      questions: [
        {
          type: "order",
          q: "Traduce: No estoy seguro. Quizás vaya.",
          words: ["I'm", "not", "sure.", "I", "might", "go."],
          a: "I'm not sure. I might go.",
          extra: ["will", "going", "to"],
          tip: "'I'm not sure' + MIGHT = double emphasis on uncertainty. Very natural combo.",
          successTip: "'I'm not sure. I might go.' = double uncertainty 🤔🤔. This combination is everywhere in English conversation. 'I'm not sure' sets the tone → MIGHT confirms it. Very polite, very non-committal. Native speakers use this combo constantly for social plans!"
        },
        {
          type: "mc",
          q: "'Will you join us for dinner?' 'I don't know. I ___ be busy.'",
          options: ["might", "will", "am going to"],
          a: "might",
          tip: "'I don't know' = uncertainty signal → MIGHT always follows.",
          successTip: "'I don't know. I might be busy.' = very natural way to hedge 💬. You're not saying no — you're leaving the door open. 'I don't know' always predicts MIGHT in the next clause. Listen for this in English conversation and you'll hear it constantly!"
        },
        {
          type: "input",
          q: "I'm not sure yet. I ___ take the job offer.",
          a: "might",
          alts: ["Might"],
          tip: "'Not sure yet' = MIGHT. You're still deciding about the job.",
          successTip: "MIGHT for big decisions in progress 💼. 'I might take the job offer' = you're genuinely considering it. This is more honest than 'I'm going to take it' (decided) or 'I'll take it' (instant). MIGHT buys thinking time without closing any doors!"
        },
        {
          type: "mc",
          q: "'Are you free this weekend?' 'Maybe. I ___ have plans.'",
          options: ["might", "will", "am"],
          a: "might",
          tip: "'Maybe' = uncertainty word. Pairs naturally with MIGHT.",
          successTip: "'Maybe' and 'might' are natural partners 🤝. Both express uncertainty. 'Maybe. I might have plans.' = very common, very honest social response. You're not sure if you have plans — maybe something will come up, maybe not. Classic use of MIGHT!"
        },
        {
          type: "order",
          q: "Traduce: No lo sé. Quizás estudie en el extranjero.",
          words: ["I", "don't", "know.", "I", "might", "study", "abroad."],
          a: "I don't know. I might study abroad.",
          extra: ["will", "going", "to"],
          tip: "'Study abroad' = estudiar en el extranjero. Big undecided plan = MIGHT.",
          successTip: "'Study abroad' = estudiar en el extranjero 🌍. Very common phrase among university students! 'I might study abroad' = you're considering it but haven't decided. Perfect use of MIGHT for big life plans in progress. 'I'm going to study abroad' would mean already decided and planned!"
        },
        {
          type: "mc",
          q: "Which sounds MOST uncertain and polite?",
          options: ["I'm not sure. I might come.", "I'll come.", "I won't come."],
          a: "I'm not sure. I might come.",
          tip: "Double uncertainty = most polite and non-committal.",
          successTip: "Politeness scale for social responses 🎩: 'I'll come' (clear yes). 'I won't come' (clear no, can sound abrupt). 'I'm not sure, I might come' (honest, polite, non-committal). The MIGHT version is often the most socially appropriate when you genuinely don't know yet!"
        },
        {
          type: "input",
          q: "'Coming tonight?' 'I haven't decided. I ___ show up.'",
          a: "might",
          alts: ["Might"],
          tip: "'Haven't decided' = uncertainty signal. Always use MIGHT after this.",
          successTip: "'Show up' = aparecer/llegar inesperadamente 🚶. 'I might show up' = quizás aparezca. Very casual, very common. 'Haven't decided yet' + MIGHT = the most natural expression of genuine indecision. 'Show up' adds a spontaneous feel — maybe I'll come, maybe not!"
        },
        {
          type: "mc",
          q: "'Want to grab coffee?' 'Possibly. I ___ have time.'",
          options: ["might", "will", "'m going to"],
          a: "might",
          tip: "'Possibly' = uncertainty. Pairs naturally with MIGHT.",
          successTip: "'Possibly. I might have time.' = honest, open response ☕. 'Possibly' is slightly more formal than 'maybe' — both signal uncertainty → both predict MIGHT. 'Possibly' appears often in workplace conversations. 'Maybe/Possibly + MIGHT' = your uncertainty toolkit!"
        },
        {
          type: "order",
          q: "Traduce: Quizás. No he decidido todavía.",
          words: ["Maybe.", "I", "haven't", "decided", "yet."],
          a: "Maybe. I haven't decided yet.",
          extra: ["might", "will", "going"],
          tip: "Two complete phrases. 'Maybe' stands alone; 'yet' goes at the end.",
          successTip: "'Maybe. I haven't decided yet.' = perfect open response 🤔. 'Haven't decided yet' = todavía no he decidido. This phrase + MIGHT in the next sentence is an incredibly natural combo. 'Haven't decided yet' is one of the most useful English expressions for buying time!"
        },
        {
          type: "match",
          q: "Match uncertainty phrases that pair with MIGHT:",
          pairs: [
            { left: "I'm not sure", right: "I might go" },
            { left: "I don't know", right: "I might stay" },
            { left: "Maybe", right: "I might try" }
          ],
          a: "completed",
          successTip: "The uncertainty trio + MIGHT 🎯: 'I'm not sure' / 'I don't know' / 'Maybe' — all three naturally predict MIGHT in the next clause. These three phrases alone cover probably 50% of all uncertainty expressions in English. Master them and MIGHT becomes second nature!"
        }
      ]
    },

    // ===================================================================
    // SET 5: MIGHT vs WILL vs GOING TO
    // ===================================================================
    {
      id: "m6-u4-s5",
      title: "Set 5: MIGHT vs WILL vs GOING TO",
      context: `*Toronto — Week 14, the group at a café*

The friends are all discussing their different plans and levels of certainty.
Dan has concert tickets: "I'm going to the show Friday — I already bought tickets."
Priya just saw a flyer: "Oh! I'll come too — I'll buy mine now!"
Sofía shrugs: "I might join you. I'm not sure yet."
Leo sighs: "I might not make it. I'm still deciding."
Four people — four different future forms!

*Toronto — Semana 14, el grupo en una cafetería*

Los amigos hablan de sus planes y sus niveles de certeza.
Dan tiene boletos para un concierto: "Voy al show el viernes — ya compré los boletos."
Priya acaba de ver un anuncio: "¡Ah! ¡Yo también voy! ¡Compro los míos ahora!"
Sofía se encoge de hombros: "Quizás me una. Todavía no estoy segura."
Leo suspira: "Quizás no pueda. Todavía estoy decidiendo."
¡Cuatro personas — cuatro formas del futuro diferentes!`,

      questions: [
        {
          type: "mc",
          q: "Dan has tickets. Tomorrow he ___ to the concert.",
          options: ["is going", "might go", "will go"],
          a: "is going",
          tip: "Tickets already bought = evidence of a pre-made plan = GOING TO.",
          successTip: "Tickets = evidence → GOING TO ✅🎫. GOING TO is for plans already decided and confirmed. MIGHT = uncertain (wrong — he has tickets!). WILL = instant decision (wrong — he bought tickets before). Physical evidence (ticket, reservation, appointment) always signals GOING TO!"
        },
        {
          type: "mc",
          q: "Priya just sees a flyer. She says: 'Oh! I ___ come too!'",
          options: ["might", "am going to", "will"],
          a: "will",
          tip: "Just saw the flyer and decided RIGHT NOW = instant decision = WILL.",
          successTip: "Just decided → WILL 💡. 'Oh!' signals surprise and instant reaction. WILL for decisions made the moment you're speaking. GOING TO would mean she'd already planned to come — but she just saw the flyer! MIGHT would mean she's unsure — but she sounds excited. It's WILL!"
        },
        {
          type: "order",
          q: "Traduce: No estoy seguro. Quizás lo intente.",
          words: ["I'm", "not", "sure.", "I", "might", "give", "it", "a", "shot."],
          a: "I'm not sure. I might give it a shot.",
          extra: ["will", "going", "to"],
          tip: "'Give it a shot' = try it (idiom). Uncertainty = MIGHT, not WILL or GOING TO.",
          successTip: "'Give it a shot' = intentarlo / darle una oportunidad 🎯. Very common idiom! Similar to 'give it a try'. 'I might give it a shot' = quizás lo intente. Notice: MIGHT + idiom = extremely natural in casual English. This phrase works for anything you're considering trying!"
        },
        {
          type: "mc",
          q: "Phone rings. You say instantly:",
          options: ["I'll answer it!", "I might answer it.", "I'm going to answer it."],
          a: "I'll answer it!",
          tip: "INSTANT DECISION = WILL. MIGHT = uncertain (wrong for a ringing phone).",
          successTip: "Instant reaction → WILL ⚡. 'I might answer it' would mean you're not sure if you'll pick up — which sounds odd for a ringing phone! WILL is for the moment of decision. If you heard the phone and instantly decided to answer — that's WILL. Not MIGHT, not GOING TO!"
        },
        {
          type: "input",
          q: "I have lots of money. I ___ definitely buy that car.",
          a: "will",
          alts: ["'ll", "Will"],
          tip: "DEFINITELY + 100% certain = WILL. MIGHT would mean you're unsure — wrong here.",
          successTip: "'Definitely' + WILL = 100% certain commitment 💰✅. 'Definitely' is your signal — when you say 'definitely', you cannot use MIGHT. 'I'll definitely buy it' / 'I definitely won't buy it' — both certain. 'I might definitely buy it' = impossible combination!"
        },
        {
          type: "mc",
          q: "Sofía saved money for months for a laptop. She ___ buy it next week.",
          options: ["is going to", "might", "will"],
          a: "is going to",
          tip: "Months of saving = a pre-made plan with evidence = GOING TO.",
          successTip: "Months of saving = clear pre-made plan → GOING TO 💻📅. The effort of saving money IS the evidence of the plan. MIGHT would say she's unsure (she's not — she saved for months!). WILL would be an instant decision (wrong — this was planned long ago). GOING TO is the only correct choice!"
        },
        {
          type: "order",
          q: "Traduce: Depende del clima. Quizás vayamos a la playa.",
          words: ["It", "depends", "on", "the", "weather.", "We", "might", "go", "to", "the", "beach."],
          a: "It depends on the weather. We might go to the beach.",
          extra: ["will", "going", "are"],
          tip: "'Depends on' = conditional/uncertain. Always leads to MIGHT (not WILL or GOING TO).",
          successTip: "'It depends on the weather. We might go to the beach.' 🏖️. 'Depends on' is the ultimate MIGHT signal. When an outcome depends on something unknown, the result is always uncertain → MIGHT. 'Depends on + MIGHT' is one of the most natural patterns in everyday English!"
        },
        {
          type: "mc",
          q: "Which shows the LEAST commitment?",
          options: ["I might go.", "I'm going to go.", "I'll definitely go."],
          a: "I might go.",
          successTip: "Commitment ladder 📊: 'I might go' (zero commitment — maybe). 'I'm going to go' (decided). 'I'll definitely go' (100% committed). MIGHT = the honest answer when you truly haven't decided. It's the least pushy, least committal option — and it's perfectly natural to use it!"
        },
        {
          type: "match",
          q: "Match the situation to the correct future form:",
          pairs: [
            { left: "Not sure yet (50%)", right: "might" },
            { left: "Already decided (evidence)", right: "going to" },
            { left: "Deciding right now (instant)", right: "will" }
          ],
          a: "completed",
          successTip: "The Module 6 grammar triangle! 🔺 MIGHT = uncertain (50%). GOING TO = pre-made plan (evidence). WILL = instant decision (right now). Three futures — three completely different meanings. Master this triangle and your English future tense is essentially complete!"
        },
        {
          type: "mc",
          q: "'I haven't decided yet.' Which future form follows naturally?",
          options: ["I might...", "I'm going to...", "I'll definitely..."],
          a: "I might...",
          tip: "Haven't decided = uncertainty. Only MIGHT fits genuine indecision.",
          successTip: "'Haven't decided yet' → MIGHT 🤔. This is a 100% reliable signal. If you haven't decided, you can't use GOING TO (decided) or WILL (deciding right now). Only MIGHT fits genuine pre-decision uncertainty. Remember: MIGHT = decision NOT yet made. The perfect honesty modal!"
        }
      ]
    },
    // ===================================================================
    // SET 6: MIGHT IN REAL SITUATIONS
    // ===================================================================
    {
      id: "m6-u4-s6",
      title: "Set 6: Real-Life MIGHT",
      context: `*Toronto — Week 14, Friday afternoon*

Dan is the most laid-back person in the group. He never stresses about plans.
"What are you doing this weekend?" Sofía asks.
"Not sure," he says. "I might just chill. I might swing by the market. I might take the day off from everything."
Sofía laughs. "You might be the most relaxed person in Canada."
Dan grins. "That's a high bar. But I might manage it."

*Toronto — Semana 14, viernes por la tarde*

Dan es la persona más tranquila del grupo. Nunca se estresa por los planes.
"¿Qué vas a hacer este fin de semana?" pregunta Sofía.
"No sé," dice. "Quizás simplemente descanse. Quizás pase por el mercado. Quizás me tome un descanso de todo."
Sofía se ríe. "Quizás seas la persona más relajada de Canadá."
Dan sonríe. "Es un estándar alto. Pero quizás lo logre."`,

      questions: [
        {
          type: "mc",
          q: "'What are you doing Saturday?' 'Not sure. I ___ just chill at home.'",
          options: ["might", "will", "am going to"],
          a: "might",
          tip: "'Not sure' = MIGHT. Also: 'just chill' = relajarse sin hacer nada (very informal).",
          successTip: "'I might just chill' = quizás simplemente descanse 😎. 'Just chill' = relax casually, do nothing special. Very common in North American English. 'Might just...' is an extremely natural pattern — it suggests a simple, low-effort option you're considering. Sounds completely native!"
        },
        {
          type: "order",
          q: "Traduce: Quizás me tome el día libre mañana.",
          words: ["I", "might", "take", "the", "day", "off", "tomorrow."],
          a: "I might take the day off tomorrow.",
          extra: ["will", "going", "have"],
          tip: "'Take the day off' = tomarse el día libre. Common work/school phrase.",
          successTip: "'Take the day off' = tomarse el día libre 📅. Essential work phrase! 'Take a day off' / 'take the day off' — both natural. 'I might take the day off' = considering it but not sure. Very common Monday-to-Friday expression. Compare: 'I'm going to take the day off' (already decided/requested)."
        },
        {
          type: "input",
          q: "This shirt is nice, but I'm not sure. I ___ think about it.",
          a: "might",
          alts: ["Might"],
          tip: "Not sure whether to buy = uncertainty = MIGHT. Very common in shopping.",
          successTip: "'I might think about it' = polite shopping non-commitment 🛍️. Shop assistants hear this constantly. It's polite — you're not saying no, just not saying yes yet. More natural than 'I don't know.' 'I might think about it' = quizás lo piense. Classic browser-not-buyer phrase!"
        },
        {
          type: "mc",
          q: "'Are you coming to the gym?' 'I ___ skip it today. I'm sore.'",
          options: ["might", "will", "am going to"],
          a: "might",
          tip: "'Skip it' = not go / saltárselo. MIGHT = you're considering skipping, not certain.",
          successTip: "'I might skip it today' = quizás me lo salte hoy 🏋️. 'Skip' = intentionally not do something. 'Skip the gym' / 'skip class' / 'skip lunch' — very common informal verb. MIGHT + skip = you're leaning towards not going but haven't decided. Very authentic everyday English!"
        },
        {
          type: "order",
          q: "Traduce: Quizás me pase por tu casa más tarde.",
          words: ["I", "might", "swing", "by", "your", "place", "later."],
          a: "I might swing by your place later.",
          extra: ["will", "going", "pass"],
          tip: "'Swing by' = pasar casualmente por un lugar. Very casual phrasal verb.",
          successTip: "'Swing by' = pasar por un lugar brevemente 🚶. Very casual! 'I might swing by your place' = quizás pase por tu casa. 'Your place' = tu casa (informal). More energetic and casual than 'I might visit you.' 'Swing by' implies a quick, unplanned stop — perfect with MIGHT!"
        },
        {
          type: "mc",
          q: "'Want dessert?' 'I'm full, but I ___ have a small bite.'",
          options: ["might", "will", "won't"],
          a: "might",
          tip: "Full but considering = MIGHT. Not certain either way.",
          successTip: "'I might have a small bite' = quizás tome un pequeño bocado 🍰. 'A small bite' = poquito. Classic post-dinner indecision! MIGHT is perfect here — you're full, but the dessert looks good. Not a yes, not a no. 'A small bite' + MIGHT = completely natural, very relatable!"
        },
        {
          type: "input",
          q: "We're thinking about Europe. We ___ check out Italy.",
          a: "might",
          alts: ["Might"],
          tip: "'Thinking about' = still considering, not decided. Use MIGHT.",
          successTip: "'Check out' = explorar/visitar informally 🗺️. 'Check out Italy' = visit and explore Italy. Very common travel expression. 'We might check out Italy' = we're considering it for our trip. 'Thinking about' + MIGHT = natural travel planning language before anything is booked!"
        },
        {
          type: "mc",
          q: "'Wanna grab drinks after work?' 'I ___ be up for it — depends on how I feel.'",
          options: ["might", "will", "am"],
          a: "might",
          tip: "'Depends on how I feel' = conditional uncertainty = MIGHT.",
          successTip: "'Be up for it' = tener ganas / estar animado 🍻. 'I might be up for it' = quizás tenga ganas. 'Wanna' = want to (very casual). 'Grab drinks' = casual drinks after work. This whole sentence is pure casual North American English — MIGHT fits perfectly because it all depends on your energy after work!"
        },
        {
          type: "order",
          q: "Traduce: Quizás pida comida. No tengo ganas de cocinar.",
          words: ["I", "might", "order", "food.", "I", "don't", "feel", "like", "cooking."],
          a: "I might order food. I don't feel like cooking.",
          extra: ["will", "going", "want"],
          tip: "'Don't feel like + verb-ing' = no tener ganas de. Very common expression.",
          successTip: "'Don't feel like + -ing' = no tener ganas de... 😴. One of the most used expressions in casual English! 'I don't feel like cooking / working / going out.' + MIGHT for what you'll do instead = extremely natural combo. 'I don't feel like cooking. I might order food.' Perfect!"
        },
        {
          type: "match",
          q: "Match casual situations with natural MIGHT phrases:",
          pairs: [
            { left: "Free weekend, no plans", right: "might just chill" },
            { left: "Visiting a friend casually", right: "might swing by" },
            { left: "Considering a purchase", right: "might think about it" }
          ],
          a: "completed",
          successTip: "Three casual MIGHT combos 😎: 'might just chill' (lazy weekend). 'might swing by' (casual visit). 'might think about it' (undecided buyer). All three are extremely common in everyday spoken English. Knowing these specific combos makes your English sound natural and fluent — not textbook!"
        }
      ]
    },

    // ===================================================================
    // SET 7: MIGHT IN QUESTIONS — CRITICAL WARNING
    // ===================================================================
    {
      id: "m6-u4-s7",
      title: "Set 7: MIGHT in Questions (Don't!)",
      context: `*Toronto — Week 15, grammar class*

Professor Chen writes on the board: "MIGHT is NOT used in questions."
She turns to the class. "Can anyone tell me why?"
Leo tries: "Might you explain, Professor?" The class winces.
"That's exactly the error," she says gently. "Could you explain, Leo — that's correct."
"But in Spanish," Sofía says, "we use 'podrías' for both possibility and questions."
"Exactly the trap," Professor Chen smiles. "In English, MIGHT is only for statements — never questions."

*Toronto — Semana 15, clase de gramática*

La profesora Chen escribe en la pizarra: "MIGHT no se usa en preguntas."
Se voltea hacia la clase. "¿Alguien puede decirme por qué?"
Leo lo intenta: "¿Might you explain, Professor?" La clase hace una mueca.
"Ese es exactamente el error," dice con calma. "Podría decirse 'Could you explain, Leo' — eso sí es correcto."
"Pero en español," dice Sofía, "usamos 'podrías' tanto para posibilidad como para preguntas."
"Exactamente la trampa," sonríe la profesora Chen. "En inglés, MIGHT es solo para afirmaciones — nunca para preguntas."`,

      questions: [
        {
          type: "mc",
          q: "Which question is GRAMMATICALLY WRONG in modern English?",
          options: ["Might you come tonight?", "Will you come tonight?", "Are you coming tonight?"],
          a: "Might you come tonight?",
          tip: "CRITICAL: MIGHT is NOT used in questions in modern English.",
          successTip: "'Might you...?' = archaic / wrong in modern English ❌. This is the #1 MIGHT error for Spanish speakers — 'podría' translates to both MIGHT and COULD, which causes confusion. In modern English: ✓ 'Will you come?' ✓ 'Are you coming?' ✓ 'Could you come?' ❌ 'Might you come?' Never!"
        },
        {
          type: "mc",
          q: "How do you correctly ask: '¿Vendrás a la fiesta?'",
          options: ["Will you come to the party?", "Might you come to the party?", "Do you might come?"],
          a: "Will you come to the party?",
          tip: "Questions about future plans = WILL you / ARE you going to. NEVER 'Might you'.",
          successTip: "Future questions use WILL or GOING TO 🎉. 'Will you come?' = ¿Vendrás? 'Are you going to come?' = ¿Vas a venir? Both correct. 'Might you come?' = wrong in modern English. 'Do you might come?' = doubly wrong (never put 'do' before a modal). Always: Will you / Are you going to!"
        },
        {
          type: "input",
          q: "Correct the error: 'Might you be free tomorrow?' → ___ you be free tomorrow?",
          a: "Will",
          alts: ["will", "Are", "are", "Could", "could", "Would", "would"],
          tip: "Replace MIGHT with WILL, ARE, COULD, or WOULD in questions.",
          successTip: "Fix: 'Might you be free?' → 'Will you be free?' ✅. Or: 'Are you free tomorrow?' / 'Could you be free?' All correct. The rule is simple: MIGHT = statements only. For questions, choose WILL, ARE (going to), COULD, or WOULD depending on the level of politeness you need!"
        },
        {
          type: "mc",
          q: "'Can you help?' — What's a more POLITE version?",
          options: ["Could you help?", "Might you help?", "Will you help?"],
          a: "Could you help?",
          tip: "COULD = polite request. NOT 'might' for politeness — MIGHT is not used in questions.",
          successTip: "Polite question scale 🎩: 'Could you help?' (very polite). 'Would you help?' (polite). 'Can you help?' (direct). 'Will you help?' (direct/slightly demanding). 'Might you help?' (wrong). COULD is the go-to for polite requests — not MIGHT. Remember: MIGHT = statements only!"
        },
        {
          type: "order",
          q: "Correct way to ask: ¿Irás al concierto?",
          words: ["Are", "you", "going", "to", "the", "concert?"],
          a: "Are you going to the concert?",
          extra: ["Might", "Will", "Could"],
          tip: "GOING TO in questions = natural and very common. MIGHT cannot be used.",
          successTip: "'Are you going to the concert?' = ¿Irás al concierto? 🎵. Very natural question form. Also correct: 'Will you go to the concert?' Both work perfectly. MIGHT cannot form questions — it's a rule without exceptions in modern English. Use GOING TO or WILL for future questions!"
        },
        {
          type: "mc",
          q: "Someone asks 'Are you coming?' You reply with honest uncertainty:",
          options: ["I might.", "Might I.", "I might come."],
          a: "I might.",
          tip: "SHORT ANSWER: 'I might' is a perfect reply! Only the question form is wrong.",
          successTip: "'I might.' = perfect short answer ✅. The ban on MIGHT in questions does NOT apply to answers! 'Are you coming?' → 'I might.' = completely natural. Short answers with MIGHT are very common: 'I might.' / 'She might.' / 'We might.' Only 'Might you?' as a question is wrong!"
        },
        {
          type: "input",
          q: "Fix the error: 'Might she be there?' → ___ she be there?",
          a: "Will",
          alts: ["will", "Would", "would", "Could", "could"],
          tip: "Replace MIGHT in the question with WILL, WOULD, or COULD.",
          successTip: "Fix: 'Might she be there?' → 'Will she be there?' ✅. Or: 'Is she going to be there?' 'Could she be there?' All correct alternatives. The pattern is always: replace question-form MIGHT with WILL / GOING TO / COULD / WOULD depending on context and politeness level!"
        },
        {
          type: "mc",
          q: "Very old-fashioned/formal 'Might I...?' was used for:",
          options: ["Asking permission (archaic)", "Asking about the future", "Asking about the past"],
          a: "Asking permission (archaic)",
          tip: "'Might I ask...?' = very old-fashioned permission request. Rarely used today.",
          successTip: "'Might I ask your name?' = very archaic/formal 🎩. You'll see this in Victorian literature and old films. In modern English: 'May I ask?' (formal) or 'Could I ask?' (polite) or 'Can I ask?' (informal). 'Might I?' is so rare today that using it sounds comical or theatrical!"
        },
        {
          type: "order",
          q: "Modern polite way: ¿Puedo preguntarte tu nombre?",
          words: ["Could", "I", "ask", "your", "name?"],
          a: "Could I ask your name?",
          extra: ["Might", "Will", "May"],
          tip: "COULD I = modern polite permission request. Not MIGHT I.",
          successTip: "'Could I ask your name?' = ¿Podría preguntarte tu nombre? 🎩. Perfect formal-but-modern phrasing. Also natural: 'May I ask your name?' (slightly more formal). 'Can I ask?' (casual). All correct. 'Might I ask?' = only in historical dramas! Stick with COULD or MAY for formal requests."
        },
        {
          type: "match",
          q: "Match the question types to the correct modal:",
          pairs: [
            { left: "Future question", right: "Will you...?" },
            { left: "Polite request", right: "Could you...?" },
            { left: "WRONG in modern English", right: "Might you...?" }
          ],
          a: "completed",
          successTip: "Question modal map 🗺️: WILL you = future question. COULD you = polite request. GOING TO = pre-made plan question. MIGHT you = wrong! This map is simple but critical. Spanish speakers confuse 'podrías' (could/might) constantly. In English: MIGHT = zero questions. No exceptions in modern usage!"
        }
      ]
    },

    // ===================================================================
    // SET 8: MIGHT vs CAN
    // ===================================================================
    {
      id: "m6-u4-s8",
      title: "Set 8: MIGHT vs CAN",
      context: `*Toronto — Week 15, Saturday at the pool*

Priya is an excellent swimmer. Sofía is not so sure about her own skills.
"Can you swim?" Priya asks.
"I can swim," says Sofía. "But I might not swim today — the water looks freezing."
Priya dives in. "You might change your mind once you try it!"
Sofía looks at the water. "I might. Or I might just watch from here."
"That," laughs Priya, "is the most MIGHT-filled sentence I've heard all week."

*Toronto — Semana 15, sábado en la piscina*

Priya es una excelente nadadora. Sofía no está tan segura de sus propias habilidades.
"¿Sabes nadar?" pregunta Priya.
"Sé nadar," dice Sofía. "Pero quizás no nade hoy — el agua parece helada."
Priya se lanza al agua. "¡Quizás cambies de opinión cuando lo intentes!"
Sofía mira el agua. "Quizás. O quizás simplemente mire desde aquí."
"Eso," se ríe Priya, "es la oración con más MIGHT que he escuchado en toda la semana."`,

      questions: [
        {
          type: "mc",
          q: "'I might go' means:",
          options: ["Maybe I'll go (possibility)", "I'm able to go (ability)", "I have permission to go"],
          a: "Maybe I'll go (possibility)",
          tip: "MIGHT = possibility (maybe). CAN = ability or permission.",
          successTip: "MIGHT vs CAN = two completely different meanings 🔑. 🤔 MIGHT = quizás/tal vez (future possibility). 💪 CAN = poder (ability). ✅ CAN = tener permiso (permission). 'I might go' = maybe I'll go. 'I can go' = I'm able to go / I'm allowed to go. Don't mix them up!"
        },
        {
          type: "mc",
          q: "Translate: 'Puedo nadar.' (ability)",
          options: ["I can swim.", "I might swim.", "I could swim."],
          a: "I can swim.",
          tip: "ABILITY = CAN! MIGHT would mean 'maybe I'll swim' (possibility — different).",
          successTip: "'I can swim' = sé nadar / puedo nadar 🏊. CAN = ability you have right now. 'I might swim' = maybe I'll swim (different — a future possibility). This is the classic confusion: Spanish 'puedo' covers both CAN (ability) and MIGHT NOT. In English they're completely separate words!"
        },
        {
          type: "order",
          q: "Traduce: Quizás nade más tarde. (possibility, not ability)",
          words: ["I", "might", "swim", "later."],
          a: "I might swim later.",
          extra: ["can", "could", "will"],
          tip: "POSSIBILITY (maybe I'll do it) = MIGHT. Not CAN (ability).",
          successTip: "'I might swim later' = quizás nade después 🏊. This is a future possibility — not about ability. You CAN swim (you know how), but you MIGHT swim later (you're not sure if you will). Same verb 'swim' — completely different meaning based on the modal!"
        },
        {
          type: "mc",
          q: "Polite permission request: '___use your phone?'",
          options: ["Could I", "Might I", "Will I"],
          a: "Could I",
          tip: "PERMISSION = CAN / COULD / MAY. MIGHT is not used for permission requests.",
          successTip: "Permission modals 🙏: 'Can I...?' (informal permission). 'Could I...?' (polite permission). 'May I...?' (formal permission). 'Might I...?' (archaic/wrong). For asking permission in modern English, always use CAN / COULD / MAY. MIGHT is only for future possibility statements!"
        },
        {
          type: "input",
          q: "Future possibility: I ___ visit Japan someday.",
          a: "might",
          alts: ["Might"],
          tip: "FUTURE POSSIBILITY (maybe someday) = MIGHT. Not CAN (ability).",
          successTip: "'I might visit Japan someday' = quizás visite Japón algún día 🗾. 'Someday' = algún día — signals a vague future possibility, perfect for MIGHT. 'I can visit Japan' would mean you're able to go (physically/financially) — different meaning. Always: future possibility = MIGHT!"
        },
        {
          type: "mc",
          q: "Ability: He ___ speak three languages.",
          options: ["can", "might", "will"],
          a: "can",
          tip: "ABILITY = CAN. 'He might speak' would mean 'maybe he'll speak' — different.",
          successTip: "CAN for abilities 💪: 'He can speak three languages' = sabe hablar tres idiomas. 'He might speak three languages' = maybe he'll speak (future possibility — different!). Always use CAN for skills and abilities. MIGHT for future uncertainty. Clear, simple, non-negotiable distinction!"
        },
        {
          type: "order",
          q: "Traduce: Quizás hable con él mañana. (future possibility)",
          words: ["I", "might", "talk", "to", "him", "tomorrow."],
          a: "I might talk to him tomorrow.",
          extra: ["can", "could", "will"],
          tip: "FUTURE POSSIBILITY = MIGHT. 'I can talk' would mean you have the ability — different.",
          successTip: "'I might talk to him tomorrow' = quizás hable con él mañana 💬. Tomorrow = future + uncertainty = MIGHT. 'I can talk to him' = I'm able to / I'm allowed to (present ability/permission). 'I'll talk to him' = I'm going to (decided). MIGHT = the only choice for uncertain future plans!"
        },
        {
          type: "mc",
          q: "What's the difference between 'I can drive' and 'I might drive to work tomorrow'?",
          options: [
            "Can = ability, Might = future possibility",
            "Both mean the same thing",
            "Can = permission, Might = ability"
          ],
          a: "Can = ability, Might = future possibility",
          successTip: "Two completely different sentences! 🚗 'I CAN drive' = sé manejar (ability you have). 'I MIGHT drive to work tomorrow' = quizás vaya manejando mañana (uncertain future plan). Same person, same car, same verb — but CAN talks about skill, MIGHT talks about a possible future action. Essential distinction!"
        },
        {
          type: "input",
          q: "She ___ play piano beautifully. (ability)",
          a: "can",
          alts: ["Can"],
          tip: "ABILITY = CAN. 'She might play' would mean 'maybe she'll play' — different.",
          successTip: "'She can play piano beautifully' = toca el piano hermosamente (habilidad) 🎹. CAN = the ability exists right now. 'She might play piano tonight' = maybe she'll play tonight (future possibility). One word changes the whole meaning. CAN = skill. MIGHT = future uncertainty. Master this!"
        },
        {
          type: "match",
          q: "Match the meaning to the correct modal:",
          pairs: [
            { left: "Future possibility (maybe)", right: "might" },
            { left: "Ability (can do it)", right: "can" },
            { left: "Permission (allowed to)", right: "can / may" }
          ],
          a: "completed",
          successTip: "Modal meaning map 🗺️: MIGHT = future uncertainty (quizás). CAN = ability (poder hacer algo). CAN/MAY = permission (tener permiso). Three different concepts — three different modals. In Spanish, 'poder' covers all three, which causes massive confusion for learners. In English, each meaning has its own word!"
        }
      ]
    },

    // ===================================================================
    // SET 9: COMMON MISTAKES
    // ===================================================================
    {
      id: "m6-u4-s9",
      title: "Set 9: Common Mistakes",
      context: `*Toronto — Week 16, error correction workshop*

Professor Chen runs a fun error-correction class. She puts wrong sentences on the board.
"These are real mistakes from English learners," she says. "Can you spot them?"
Sofía looks at: "Might you come tonight?" / "She mights come." / "I might to go."
"I see them all!" she says, raising her hand.
Leo whispers: "You might be the best student here, Sofía."
"You might be right," she whispers back. "Or I might just have a good teacher."

*Toronto — Semana 16, taller de corrección de errores*

La profesora Chen dirige una divertida clase de corrección de errores. Pone oraciones incorrectas en la pizarra.
"Estos son errores reales de estudiantes de inglés," dice. "¿Pueden identificarlos?"
Sofía ve: "Might you come tonight?" / "She mights come." / "I might to go."
"¡Los veo todos!" dice, levantando la mano.
Leo susurra: "Quizás seas la mejor estudiante aquí, Sofía."
"Quizás tengas razón," susurra ella. "O quizás solo tenga una buena maestra."`,

      questions: [
        {
          type: "mc",
          q: "Which sentence is INCORRECT?",
          options: ["Might you come tonight?", "I might come tonight.", "She might not come."],
          a: "Might you come tonight?",
          tip: "NEVER 'Might you...?' in modern English. Use 'Will you...?' instead.",
          successTip: "Error #1 for Spanish speakers: 'Might you...?' ❌. MIGHT is NEVER used in questions. 'Will you come tonight?' ✓ 'Are you coming tonight?' ✓ 'Might you come tonight?' ❌ This error comes from translating 'podrías' directly. Don't — in English MIGHT is statements only!"
        },
        {
          type: "mc",
          q: "Fix the error: 'I might will go' →",
          options: ["I might go", "I will might go", "I might to go"],
          a: "I might go",
          tip: "NEVER two modals together! MIGHT already expresses possibility — no WILL needed.",
          successTip: "One modal at a time — always! ⚠️ MIGHT already gives you the future possibility meaning. Adding WILL is redundant and wrong. ❌ 'might will' ❌ 'might going to' ❌ 'might to' → ✅ MIGHT + base verb. Clean, simple, one modal. This rule applies to all modals in English!"
        },
        {
          type: "input",
          q: "Fix: 'She mights come' → She ___ come.",
          a: "might",
          alts: ["Might"],
          tip: "MIGHT never changes form. NO -s for third person. Ever.",
          successTip: "Modal verbs NEVER add -s for third person! ❌ 'she mights' ❌ 'he wills' ❌ 'she cans' → ✅ 'she might' ✅ 'he will' ✅ 'she can'. This is one of the most important rules about modal verbs. They NEVER change form regardless of the subject — no -s, no -ing, nothing!"
        },
        {
          type: "mc",
          q: "Fix the error: 'I might to go' →",
          options: ["I might go", "I might going", "I might to going"],
          a: "I might go",
          tip: "MIGHT + BASE VERB. No 'to' after MIGHT. Ever.",
          successTip: "MIGHT + BASE VERB — no 'to' ✅. ❌ 'might to go' ❌ 'might to eat' ❌ 'might to study'. This comes from confusing MIGHT with 'I want TO go' or 'I have TO go'. But modal verbs (MIGHT, WILL, CAN, SHOULD) never use 'to'. MIGHT + base verb. That's the complete rule!"
        },
        {
          type: "order",
          q: "Write the correct sentence: Quizás él venga.",
          words: ["He", "might", "come."],
          a: "He might come.",
          extra: ["mights", "to", "will"],
          tip: "No -s on MIGHT! He might (not 'he mights').",
          successTip: "'He might come' = quizás él venga ✅. Three words, perfectly formed. 'He mights come' ❌ (no -s). 'He might to come' ❌ (no 'to'). 'He might will come' ❌ (no double modal). Simple is correct: subject + MIGHT + base verb. Nothing more needed!"
        },
        {
          type: "mc",
          q: "'Puedo ir' (ability) vs 'Quizás vaya' (possibility) in English:",
          options: ["I can go / I might go", "I might go / I can go", "I can go / I can go"],
          a: "I can go / I might go",
          tip: "ABILITY = CAN. POSSIBILITY = MIGHT. Two different Spanish meanings = two different English words.",
          successTip: "The Spanish 'poder' trap 🇲🇽🇺🇸: 'PUEDO ir' (ability) = 'I CAN go'. 'QUIZÁS vaya' (possibility) = 'I MIGHT go'. Spanish uses context to distinguish; English uses different words. This is the root cause of most MIGHT/CAN errors. Always ask yourself: ability or possibility?"
        },
        {
          type: "input",
          q: "Fix: 'Do you might come?' → ___ you come?",
          a: "Will",
          alts: ["will", "Are", "are", "Could", "could"],
          tip: "No auxiliary DO with modal verbs! Use WILL / ARE for future questions.",
          successTip: "Never use DO/DOES with modal verbs! ❌ 'Do you might...?' ❌ 'Does she might...?' ❌ 'Do you will...?'. Modals form their own questions by inversion: WILL you / COULD you / MIGHT I (archaic). 'Do you might' = double error — both 'do' with a modal AND MIGHT in a question. Two mistakes in three words!"
        },
        {
          type: "mc",
          q: "Fix the error: 'I'm might going' →",
          options: ["I might go", "I'm going", "I might be going"],
          a: "I might go",
          tip: "Keep it simple: MIGHT + base verb. 'I'm might going' mixes two different forms.",
          successTip: "'I might go' = the simplest and most correct form ✅. 'I'm might going' mixes present continuous ('I'm going') with MIGHT incorrectly. 'I might be going' is grammatically possible but wordy. Simple is best: MIGHT + base verb. Avoid mixing 'am/is/are' with MIGHT!"
        },
        {
          type: "order",
          q: "Write the correct negative: Quizás no vaya.",
          words: ["I", "might", "not", "go."],
          a: "I might not go.",
          extra: ["don't", "won't", "mightn't"],
          tip: "MIGHT NOT = two words. NOT goes between MIGHT and the base verb.",
          successTip: "'I might not go' = quizás no vaya ✅. Structure: MIGHT + NOT + base verb. ❌ 'I don't might go' (wrong — don't with modal). ❌ 'I mightn't go' (rare in American English). ✅ 'I might not go' = standard everywhere. Simple two-word negative — clean and correct!"
        },
        {
          type: "match",
          q: "Match the errors to their corrections:",
          pairs: [
            { left: "Might you go?", right: "Will you go?" },
            { left: "I might will go", right: "I might go" },
            { left: "She mights come", right: "She might come" }
          ],
          a: "completed",
          successTip: "Top 3 MIGHT errors — now fixed! 🎉 ❌ 'Might you?' → ✅ 'Will you?' ❌ 'might will' → ✅ 'might' ❌ 'she mights' → ✅ 'she might'. These three errors come from direct translation from Spanish, adding unnecessary auxiliaries, and forgetting the no-s rule. Avoid all three and your MIGHT is perfect!"
        }
      ]
    },
  ],

  // ===================================================================
  // READING SET
  // ===================================================================
  readingSet: {
    id: "m6-u4-reading",
    title: "Reading: Alex's Uncertain Weekend",
    category: "reading",
    questions: [

      // ---------------------------------------------------------------
      // PASSAGE 1: Saturday plans
      // ---------------------------------------------------------------
      {
        type: "passage",
        content: `**Alex's Uncertain Weekend**

Hi! I'm Alex. Unlike last week when I had everything planned, this weekend is completely open — and honestly, I'm not sure what I'll do yet!

Saturday morning, I might sleep in. I've been so tired lately. Or I might go to the farmers' market — there's a new cheese stand I want to check out. I can't decide.

My friend Jamie texted: "Want to hike Saturday?" I replied: "I might join you — depends on the weather." Jamie knows me well. She said: "Translation: you might not come." She's probably right.

---

*¡Hola! Soy Alex. A diferencia de la semana pasada cuando tenía todo planeado, este fin de semana está completamente libre — y honestamente, ¡todavía no sé qué haré!*

*El sábado por la mañana, quizás me quede dormido. He estado muy cansado últimamente. O quizás vaya al mercado de productores — hay un nuevo puesto de quesos que quiero visitar. No puedo decidir.*

*Mi amiga Jamie me escribió: "¿Quieres ir de excursión el sábado?" Respondí: "Quizás me una — depende del clima." Jamie me conoce bien. Dijo: "Traducción: quizás no vengas." Probablemente tenga razón.*`
      },
      {
        type: "mc",
        q: "Why does Alex use MIGHT for his Saturday plans?",
        options: [
          "He has already decided what to do.",
          "He genuinely hasn't decided yet — both options are open.",
          "He doesn't want to go to the farmers' market."
        ],
        a: "He genuinely hasn't decided yet — both options are open.",
        tip: "MIGHT = genuine uncertainty. Alex hasn't made a decision at all.",
        successTip: "MIGHT = decision not made yet 🤔. Alex has two options (sleep in OR market) and is undecided about both. This is the perfect MIGHT situation — not leaning towards either option yet. When you genuinely don't know what you'll do, MIGHT is always the most honest and natural choice!"
      },
      {
        type: "input",
        q: "Alex told Jamie: 'I might join you — ___ on the weather.'",
        a: "depends",
        alts: ["Depends", "depending"],
        tip: "'Depends on' = depende de. Classic MIGHT companion phrase.",
        successTip: "'It depends on the weather' = depende del clima 🌦️. 'Depends on' + MIGHT = one of the most natural pairings in English. When an outcome depends on something unknown (weather, how you feel, traffic), MIGHT is almost always the correct modal to follow. Very authentic everyday English!"
      },
      {
        type: "mc",
        q: "Jamie says 'Translation: you might not come.' What does this reveal about MIGHT NOT?",
        options: [
          "MIGHT NOT = leaning towards no, not a definite refusal.",
          "MIGHT NOT = the same as WON'T (definite no).",
          "MIGHT NOT = definitely going."
        ],
        a: "MIGHT NOT = leaning towards no, not a definite refusal.",
        tip: "Jamie understands that 'might join' often means 'probably won't'. MIGHT NOT ≈ 25%.",
        successTip: "Jamie is a native speaker — she knows the social code! 🎯 'I might join you' in casual English often signals 'probably not'. 'I might not come' = leaning towards no (25%). This is the real-world nuance of MIGHT NOT — it's softer than WON'T but often signals the same intention in social contexts!"
      },

      // ---------------------------------------------------------------
      // PASSAGE 2: Saturday afternoon
      // ---------------------------------------------------------------
      {
        type: "passage",
        content: `**Saturday Afternoon Possibilities**

By noon I still haven't decided anything. The weather is cloudy — it might rain, it might not. Perfect MIGHT weather.

I check my phone. Three messages:
- Marco: "Movie tonight? I might get tickets."
- Sam: "Gym at 3? I might skip — I'm sore."
- Jamie: "Still coming hiking? We might head out at 2."

I reply to all three: "I might." Classic Alex.

I can make decisions when I have to — I can drive, I can cook, I can handle emergencies. But for optional weekend plans? I might never decide.

---

*A mediodía todavía no he decidido nada. El clima está nublado — quizás llueva, quizás no. Clima perfecto para el MIGHT.*

*Reviso mi teléfono. Tres mensajes:*
*- Marco: "¿Película esta noche? Quizás compre boletos."*
*- Sam: "¿Gimnasio a las 3? Quizás me lo salte — estoy adolorido."*
*- Jamie: "¿Sigues viniendo a la excursión? Quizás salgamos a las 2."*

*Respondo a los tres: "Quizás." El Alex clásico.*

*Puedo tomar decisiones cuando es necesario — sé manejar, sé cocinar, sé manejar emergencias. Pero ¿para planes opcionales de fin de semana? Quizás nunca decida.*`
      },
      {
        type: "mc",
        q: "Alex says 'I can make decisions when I have to — I can drive, I can cook.' What does CAN show here?",
        options: [
          "Future possibility — maybe he'll drive or cook.",
          "Ability — skills he has.",
          "Permission — he is allowed to drive and cook."
        ],
        a: "Ability — skills he has.",
        tip: "CAN = ability (skills you possess). MIGHT = possibility (maybe in the future).",
        successTip: "'I can drive / cook' = sé manejar / cocinar (abilities) 💪. CAN for abilities is always present tense — it's about what you know how to do right now. Compare: 'I might drive tomorrow' (future possibility — not about skill). Same verb 'drive' — CAN (skill) vs MIGHT (uncertain future). Never confuse them!"
      },
      {
        type: "order",
        q: "Reconstruct Sam's message to Alex:",
        words: ["Gym", "at", "3?", "I", "might", "skip", "—", "I'm", "sore."],
        a: "Gym at 3? I might skip — I'm sore.",
        extra: ["won't", "will", "going"],
        tip: "'Skip' = not go intentionally. MIGHT = still considering — not 100% decided.",
        successTip: "'I might skip — I'm sore' = quizás me lo salte — estoy adolorido 🏋️. 'Skip' = saltarse algo intencionalmente. Notice: MIGHT, not WON'T — Sam isn't certain yet. 'I'm sore' is the reason but not a definite barrier. Classic text message English: short, honest, and appropriately uncertain!"
      },
      {
        type: "mc",
        q: "Alex replies 'I might' to all three messages. This short answer is:",
        options: [
          "Correct — short answers with MIGHT are perfectly natural.",
          "Wrong — MIGHT cannot be used as a short answer.",
          "Wrong — he needs to say 'I might go' in full."
        ],
        a: "Correct — short answers with MIGHT are perfectly natural.",
        tip: "Short answers with MIGHT are 100% natural and common in English.",
        successTip: "'I might.' = perfect short answer ✅. You don't need to repeat the full verb in a short answer. 'Are you coming?' → 'I might.' (= I might come). 'Will you go?' → 'I might.' Extremely natural and efficient. Short MIGHT answers are everywhere in texting and casual speech!"
      },

      // ---------------------------------------------------------------
      // PASSAGE 3: Evening decision
      // ---------------------------------------------------------------
      {
        type: "passage",
        content: `**Sunday Morning Reflection**

Sunday morning. I'm writing this from my couch. What did I do Saturday?

I didn't go hiking — it rained. I might have gone if the weather had been better.
I didn't go to the gym — Sam and I both skipped it.
I did go to the farmers' market — the cheese was amazing.
I did see the movie with Marco — great decision.

So in the end, I made two decisions. Not bad for someone who might be the world's most indecisive person.

Next weekend? I might plan ahead. Or I might not. We'll see.

---

*Domingo por la mañana. Escribo esto desde mi sofá. ¿Qué hice el sábado?*

*No fui a la excursión — llovió. Quizás hubiera ido si el clima hubiera sido mejor.*
*No fui al gimnasio — Sam y yo nos lo saltamos.*
*Sí fui al mercado de productores — el queso estuvo increíble.*
*Sí vi la película con Marco — fue una gran decisión.*

*Así que al final tomé dos decisiones. No está mal para alguien que quizás sea la persona más indecisa del mundo.*

*¿El próximo fin de semana? Quizás lo planee con anticipación. O quizás no. Ya veremos.*`
      },
      {
        type: "mc",
        q: "Alex says 'I might plan ahead' for next weekend. This means:",
        options: [
          "He is considering planning ahead but hasn't decided.",
          "He has already decided to plan ahead.",
          "He definitely won't plan ahead."
        ],
        a: "He is considering planning ahead but hasn't decided.",
        tip: "MIGHT = still considering. No decision made = no GOING TO or WILL.",
        successTip: "'I might plan ahead' = lo estoy considerando pero no he decidido 📅. After the whole story, Alex stays true to form — even his reflection on next weekend uses MIGHT. This is perfect: when you haven't decided, MIGHT is the honest choice. 'We'll see' at the end confirms the uncertainty!"
      },
      {
        type: "input",
        q: "Alex ends with: 'I might plan ahead. Or I might ___. We'll see.'",
        a: "not",
        alts: ["Not"],
        tip: "Opposite of 'I might plan ahead' = 'I might not'. Two-word negative.",
        successTip: "'I might plan ahead. Or I might not.' = quizás lo planee. O quizás no 🤷. The MIGHT / MIGHT NOT parallel structure is very common for expressing two uncertain options. 'We'll see' = ya veremos — one of the most useful phrases for genuine uncertainty about the future!"
      },
      {
        type: "match",
        q: "Match Alex's weekend decisions to the outcome:",
        pairs: [
          { left: "Hiking (it rained)", right: "Didn't go — weather decided" },
          { left: "Farmers' market", right: "Went — great cheese" },
          { left: "Movie with Marco", right: "Went — great decision" }
        ],
        a: "completed",
        successTip: "The irony of MIGHT: sometimes the decision gets made for you! ☔ Alex's MIGHT about hiking was resolved by rain. His MIGHT about the market and movie became YES. This is real life — MIGHT is honest because the future IS uncertain. Sometimes external factors decide, sometimes you do. That's why MIGHT exists!"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET
  // ===================================================================
  reviewSet: {
    id: "m6-u4-review",
    title: "Review: Future Possibilities with MIGHT",
    questions: [
      {
        type: "mc",
        q: "Which sentence correctly expresses a 50% future possibility?",
        options: ["I might go.", "I'll go.", "I won't go."],
        a: "I might go.",
        tip: "MIGHT = 50% uncertainty. The middle ground between WILL (100%) and WON'T (0%).",
        successTip: "MIGHT = the 50% modal 🎯. 'I might go' = genuinely undecided. The full scale: WON'T (0%) → MIGHT NOT (25%) → MIGHT (50%) → WILL (100%). You now control the complete certainty spectrum. Choose the modal that honestly reflects how sure you actually are!"
      },
      {
        type: "match",
        q: "Match the certainty levels to the correct forms:",
        pairs: [
          { left: "0% — definite no", right: "won't" },
          { left: "50% — genuinely unsure", right: "might" },
          { left: "100% — definite yes", right: "will" }
        ],
        a: "completed",
        successTip: "The complete certainty scale mastered ✅. WON'T / MIGHT / WILL — three forms, three certainty levels. Add MIGHT NOT (25%) and GOING TO (planned) and you have complete control of English future expression. This is foundational — use it every time you speak about the future!"
      },
      {
        type: "order",
        q: "Traduce: No estoy segura. Quizás me quede en casa.",
        words: ["I'm", "not", "sure.", "I", "might", "stay", "home."],
        a: "I'm not sure. I might stay home.",
        extra: ["will", "going", "to"],
        tip: "'I'm not sure' + MIGHT = classic double uncertainty. Very natural.",
        successTip: "'I'm not sure. I might stay home.' = 100% natural everyday English 🏠. The two-sentence pattern (uncertainty + MIGHT) is everywhere in daily conversation. Master this combo and uncertainty sounds fluent and natural — not hesitant or awkward. Very common in texting too!"
      },
      {
        type: "mc",
        q: "Which question is CORRECT in modern English?",
        options: ["Will you come tonight?", "Might you come tonight?", "Do you might come tonight?"],
        a: "Will you come tonight?",
        tip: "MIGHT is never used in questions. Use WILL / ARE GOING TO for future questions.",
        successTip: "MIGHT = statements only, never questions ❌🚫. 'Will you come?' ✓ 'Are you going to come?' ✓ 'Might you come?' ❌ This rule has no exceptions in modern English. 'Do you might' is doubly wrong — no DO with modal verbs AND no MIGHT in questions. Always use WILL or GOING TO for questions!"
      },
      {
        type: "input",
        q: "He's considering a new job but hasn't decided. He ___ take the offer.",
        a: "might",
        alts: ["Might"],
        tip: "Considering but not decided = MIGHT. Not GOING TO (that would mean decided).",
        successTip: "'He might take the offer' = está considerando aceptar la oferta 💼. Key word: 'considering'. If he had decided → GOING TO. If he just decided in that moment → WILL. If he's genuinely undecided → MIGHT. Context + the right modal = precise, natural English every time!"
      },
      {
        type: "mc",
        q: "What's the difference between 'I can swim' and 'I might swim later'?",
        options: [
          "Can = ability I have now; Might = possible future action.",
          "Both mean the same thing.",
          "Can = future plan; Might = current ability."
        ],
        a: "Can = ability I have now; Might = possible future action.",
        tip: "CAN = ability (skill). MIGHT = future possibility (maybe I'll do it).",
        successTip: "CAN vs MIGHT = ability vs possibility 🔑. 'I can swim' = I know how (present skill). 'I might swim later' = maybe I'll swim (future uncertainty). Same verb 'swim', completely different meaning. This confusion comes from Spanish 'poder' = both can/might. In English, they're always separate!"
      },
      {
        type: "order",
        q: "Traduce: Depende del clima. Quizás no vayamos a la playa.",
        words: ["It", "depends", "on", "the", "weather.", "We", "might", "not", "go", "to", "the", "beach."],
        a: "It depends on the weather. We might not go to the beach.",
        extra: ["won't", "will", "going"],
        tip: "'Depends on' always signals MIGHT or MIGHT NOT — never WILL or GOING TO.",
        successTip: "'Depends on + MIGHT NOT' = leaning towards not going 🏖️❌. 'Depends on the weather' is the situation → MIGHT NOT is the uncertain result. Compare: 'We won't go' (100% no) vs 'We might not go' (probably not, but still possible). Weather uncertainty = MIGHT NOT almost always!"
      },
      {
        type: "mc",
        q: "Which sentence has a grammatical error?",
        options: ["She mights come tomorrow.", "She might come tomorrow.", "She might not come tomorrow."],
        a: "She mights come tomorrow.",
        tip: "Modal verbs NEVER add -s for third person. 'She might' — never 'she mights'.",
        successTip: "'She might come' ✅ / 'She mights come' ❌. Modal verbs (MIGHT, WILL, CAN, SHOULD, WOULD) NEVER change form. No -s, no -ing, no -ed — ever. 'He mights' ❌ 'She cans' ❌ 'It wills' ❌. This is one of the clearest rules in English — modals are invariable. Always!"
      },
      {
        type: "input",
        q: "'Are you going to the party?' 'I haven't decided. I ___ show up later.'",
        a: "might",
        alts: ["Might"],
        tip: "'Haven't decided' = genuine uncertainty. Always signals MIGHT.",
        successTip: "'I might show up later' = quizás aparezca más tarde 🎉. 'Haven't decided' is the key signal — it eliminates GOING TO (already decided) and WILL (deciding right now). Only MIGHT fits genuine pre-decision uncertainty. 'Show up' = aparecer — casual, very common in social contexts!"
      },
      {
        type: "match",
        q: "Match the errors to their corrections:",
        pairs: [
          { left: "I might will go", right: "I might go" },
          { left: "Might you help?", right: "Could you help?" },
          { left: "I might to eat", right: "I might eat" }
        ],
        a: "completed",
        successTip: "Unit 4 error-free checklist! ✅ No double modals ('might will'). ✅ No MIGHT in questions ('might you?'). ✅ No 'to' after MIGHT ('might to eat'). Fix these three errors and your MIGHT is perfect. You now know MIGHT for possibility, MIGHT NOT for negative uncertainty, and all the rules to use it correctly!"
      }
    ]
  }
};
